import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { easyRetailApi, type EasyRetailGap, type EasyRetailReportListItem } from "../../api/easyRetailApi";
import { useAuth } from "../../auth/AuthContext";
import { canAccessEasyRetailReports } from "../../auth/roles";

function toLocal(value?: string | null) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function statusBadge(status?: string) {
  const s = String(status || "").toUpperCase();
  if (s === "OK") return "text-bg-success";
  if (s === "PARTIAL") return "text-bg-warning";
  if (s === "ERROR") return "text-bg-danger";
  return "text-bg-secondary";
}

function severityBadge(severity: string) {
  const s = String(severity || "").toUpperCase();
  if (s === "HIGH") return "text-bg-danger";
  if (s === "MEDIUM") return "text-bg-warning";
  if (s === "LOW") return "text-bg-secondary";
  return "text-bg-info";
}

function GapTable({ gaps }: { gaps: EasyRetailGap[] }) {
  if (!gaps.length) {
    return <div className="text-muted">Nessun gap rilevato.</div>;
  }
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th>Severità</th>
            <th>Area</th>
            <th>Gap</th>
            <th>Easy Retail</th>
            <th>NAT / Corevian</th>
            <th>Azione suggerita</th>
          </tr>
        </thead>
        <tbody>
          {gaps.map((g) => (
            <tr key={g.id}>
              <td><span className={`badge ${severityBadge(g.severity)}`}>{g.severity}</span></td>
              <td>{g.area}</td>
              <td>
                <div className="fw-semibold">{g.title}</div>
                <div className="small text-muted">{g.detail}</div>
              </td>
              <td className="small">{g.easyRetailStatus || "-"}</td>
              <td className="small">{g.natStatus || "-"}</td>
              <td className="small">{g.action || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportList({ onSelect }: { onSelect: (id: string) => void }) {
  const { token } = useAuth();
  const [items, setItems] = useState<EasyRetailReportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await easyRetailApi.list(token, 50);
      setItems(res.items || []);
    } catch (e: any) {
      const msg = e?.message || "Errore caricamento report";
      setError(msg.includes("403") ? "Accesso negato. Chiedi profilo Operatore/Manager/Admin." : msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="h6 mb-0">Report salvati</h2>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => void load()} disabled={loading}>
            Aggiorna
          </button>
        </div>
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Data</th>
                <th>POS</th>
                <th>Host</th>
                <th>Stato</th>
                <th>Riepilogo</th>
                <th>Gap</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-muted">
                    {loading ? "Caricamento..." : "Nessun report. Avvia analisi da NATPOS sulla cassa."}
                  </td>
                </tr>
              ) : null}
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="small">{toLocal(r.analyzedAt)}</td>
                  <td>
                    <div>{r.posCode || "-"}</div>
                    <div className="small text-muted">{r.location || ""}</div>
                  </td>
                  <td className="small">{r.hostname || "-"}</td>
                  <td><span className={`badge ${statusBadge(r.status)}`}>{r.status}</span></td>
                  <td className="small">{r.summary?.headline || "-"}</td>
                  <td className="small">
                    H:{r.summary?.gapCounts?.high ?? 0} M:{r.summary?.gapCounts?.medium ?? 0}
                  </td>
                  <td>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onSelect(r.id)}>
                      Dettaglio
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReportDetail({ reportId }: { reportId: string }) {
  const { token } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof easyRetailApi.getById>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token || !reportId) return;
    setLoading(true);
    setError("");
    easyRetailApi
      .getById(token, reportId)
      .then(setData)
      .catch((e: any) => setError(e?.message || "Errore"))
      .finally(() => setLoading(false));
  }, [token, reportId]);

  const report = data?.report;
  const gaps = useMemo(() => (Array.isArray(report?.gaps) ? report!.gaps : []), [report]);
  const findings = (report?.findings || {}) as Record<string, any>;
  const natSnapshot = (report?.natSnapshot || {}) as Record<string, any>;

  if (loading) return <div className="text-muted">Caricamento report...</div>;
  if (error) return <div className="alert alert-danger py-2">{error}</div>;
  if (!report) return null;

  return (
    <div className="d-grid gap-3">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
            <div>
              <h2 className="h5 mb-1">Report analisi Easy Retail</h2>
              <div className="text-muted small">
                {report.posCode || "-"} · {report.hostname || "-"} · {toLocal(report.analyzedAt)}
              </div>
            </div>
            <span className={`badge ${statusBadge(report.status)} align-self-start`}>{report.status}</span>
          </div>
          <p className="mb-0">{(report.summary as any)?.headline || "Analisi completata"}</p>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h3 className="h6">Easy Retail (locale)</h3>
              <ul className="small mb-0">
                <li>DB Firebird: {(report.summary as any)?.firebirdDbFound ? "trovato" : "non trovato"}</li>
                <li>Ticket bilancia: {(report.summary as any)?.scaleTicketsFound ?? 0}</li>
                <li>WAAGE: {(report.summary as any)?.waageDetected ? "rilevato" : "non rilevato"}</li>
                <li>Parser bilancia: {(report.summary as any)?.waageParserProfile || findings.waage?.suggestedParserProfile || "-"}</li>
                <li>Path ISI rilevati: {(findings.installPaths || []).length}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h3 className="h6">Corevian (ultimi 7 gg)</h3>
              <ul className="small mb-0">
                <li>PosSale: {natSnapshot.last7Days?.posSales ?? 0}</li>
                <li>Da Easy Retail (ER-*): {natSnapshot.last7Days?.easyRetailSales ?? 0}</li>
                <li>Pagamenti: {natSnapshot.last7Days?.posPayments ?? 0}</li>
                <li>Movimenti cassetto: {natSnapshot.last7Days?.cashMovements ?? 0}</li>
                <li>Cassetto Easy Retail: {natSnapshot.last7Days?.easyRetailCashMovements ?? 0}</li>
                <li>Doc Midware: {natSnapshot.last7Days?.midwareDocuments ?? 0}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h3 className="h6">Runtime NAT</h3>
              <ul className="small mb-0">
                <li>Sola lettura: {natSnapshot.runtime?.readOnly ? "sì" : "no"}</li>
                <li>Shadow sales: {natSnapshot.runtime?.shadowSalesEnabled ? "sì" : "no"}</li>
                <li>Sync movimenti: {natSnapshot.runtime?.movementsSyncEnabled ? "sì" : "no"}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {(findings.waage as any)?.detected ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h3 className="h6">Software WAAGE (assistenza bilancia)</h3>
            <ul className="small mb-0">
              <li>Percorso ticket suggerito: {(findings.waage as any)?.suggestedScalePath || "-"}</li>
              <li>Parser NATPOS: {(findings.waage as any)?.suggestedParserProfile || "AUTO"}</li>
              {((findings.waage as any)?.executables || []).slice(0, 5).map((e: { name: string; path: string }, i: number) => (
                <li key={i}>{e.name} — <code className="small">{e.path}</code></li>
              ))}
            </ul>
            {((findings.waage as any)?.integrationHints || []).length > 0 ? (
              <ul className="small text-muted mt-2 mb-0">
                {((findings.waage as any).integrationHints as string[]).map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="card shadow-sm">
        <div className="card-body">
          <h3 className="h6">Gap e correzioni da fare</h3>
          <GapTable gaps={gaps} />
        </div>
      </div>

      {report.recommendations?.nextSteps?.length ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h3 className="h6">Prossimi passi</h3>
            <ol className="small mb-0">
              {report.recommendations.nextSteps.map((step, idx) => (
                <li key={idx}>
                  <strong>[{step.priority}]</strong> {step.title} — {step.action}
                </li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function EasyRetailAnalysisPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const canView = canAccessEasyRetailReports(user?.role);
  const [selectedId, setSelectedId] = useState<string | null>(id || null);

  useEffect(() => {
    if (id) setSelectedId(id);
  }, [id]);

  if (!canView) {
    return (
      <div className="container-fluid py-3">
        <div className="alert alert-warning mb-0">
          <strong>Analisi Easy Retail non disponibile</strong> per il profilo {user?.role || "VIEWER"}.
          Serve account <strong>Operatore</strong>, <strong>Manager</strong> o <strong>Admin</strong>.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Analisi Easy Retail</h1>
          <p className="text-muted mb-0 small">
            Report generati da NATPOS sulla cassa: cosa fa Easy Retail, cosa manca in NAT e dove intervenire.
          </p>
        </div>
        {selectedId ? (
          <Link to="/reports/easy-retail" className="btn btn-sm btn-outline-secondary">
            Torna all&apos;elenco
          </Link>
        ) : (
          <Link to="/reports" className="btn btn-sm btn-outline-secondary">
            Tutti i report
          </Link>
        )}
      </div>

      {!selectedId ? (
        <>
          <div className="alert alert-info py-2 mb-3 small">
            Avvia <strong>Analizza Easy Retail</strong> da NATPOS Electron sul PC cassa. Il report viene salvato qui
            in Corevian, menu <strong>Report → Analisi Easy Retail</strong> (<code>/reports/easy-retail</code>).
          </div>
          <ReportList onSelect={setSelectedId} />
        </>
      ) : (
        <ReportDetail reportId={selectedId} />
      )}
    </div>
  );
}
